/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState, ReactNode, useContext } from 'react';
import { Form, FormRenderProps, FormSpy } from 'react-final-form';
import { FormState } from 'final-form';
import { get } from 'lodash';

import { Dropdown, TextInput, Validator } from '@blend/enterprise-components';
import { completeTask } from 'blb-api/tasks';
import { MeContext } from 'blb-contexts/Me';
import { errorMessage } from 'blb-utils/errors';
import { BlendBridge } from 'blb-types/blendBridge';

import {
  createTaskCompletePayload,
  emptyAddress,
  initializeFormData,
  TaskDisplayStates,
} from './ScheduleClosingAppointment/ScheduleClosingTaskUtils';
import {
  renderAppointmentInformation,
  renderNotaryInformation,
} from './ScheduleClosingAppointment/ScheduleClosingTaskForm';
import { renderConfirmationScreen } from './ScheduleClosingAppointment/ScheduleClosingTaskConfirmation';
import { TaskModal, TaskModalProps } from '../TaskModal';
import { Task } from './Task';

export namespace ScheduleClosingAppointmentTask {
  export interface Props extends BlendBridge.API.ScheduleClosingAppointmentTask {
    onSuccess: () => Promise<any>;
  }

  export interface FormInputOptions {
    disabled?: boolean;
    hasPII: boolean;
    inputType?: TextInput.Type;
    label: string;
    name: string;
    options?: Dropdown.Option[];
    placeholder?: string;
    setErrorDispatch: React.Dispatch<React.SetStateAction<string | undefined>>;
    validate?: Validator;
    valueRequired?: boolean;
  }

  export type FormValues = BlendBridge.API.ScheduleClosingTaskCompleteMeta;

  export type TaskDisplayState = TaskDisplayStates.EDIT | TaskDisplayStates.CONFIRM;
}

export const ScheduleClosingAppointmentTask: React.FC<ScheduleClosingAppointmentTask.Props> = (
  props: ScheduleClosingAppointmentTask.Props,
) => {
  const me = useContext(MeContext);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [displayState, setDisplayState] = useState<ScheduleClosingAppointmentTask.TaskDisplayState>(
    TaskDisplayStates.EDIT,
  );

  const {
    buttonLabel = 'Schedule',
    borrowerPreferredAddress,
    closingDate,
    isDisabled,
    onSuccess,
    taskHeader,
    taskId,
  } = props;

  const initialFormData = initializeFormData(props);

  const [formData, setFormData] = useState<BlendBridge.API.ScheduleClosingTaskCompleteMeta>(initialFormData);

  useEffect(() => {
    if (displayState === TaskDisplayStates.CONFIRM) {
      setLoading(false);
    }
  }, [displayState]);

  const openModal = () => {
    setModalOpen(true);
  };

  const handleClose = () => {
    setError(undefined);
    setModalOpen(false);
  };

  const onSubmit = async (formValues: ScheduleClosingAppointmentTask.FormValues) => {
    if (displayState === TaskDisplayStates.EDIT) {
      setFormData(formValues);
      setDisplayState(TaskDisplayStates.CONFIRM);
      return;
    }

    try {
      setLoading(true);

      // construct payload to send to EClose
      const taskCompletePayload = createTaskCompletePayload(formValues, closingDate);

      const taskBody: BlendBridge.API.CompleteTaskBody = {
        meta: taskCompletePayload,
      };

      await completeTask(taskId ?? '', taskBody);
      await onSuccess();
    } catch (error) {
      const errorString = get(error, 'response.data.error.message', errorMessage('SENDING_FORM', me?.principalType));

      setError(errorString);
      setLoading(false);
    }
  };

  const onBack = () => {
    setDisplayState(TaskDisplayStates.EDIT);
  };

  const onFormChange = (props: FormState<BlendBridge.API.ScheduleClosingTaskCompleteMeta>) => {
    setError(undefined);

    const {
      values: { appointmentInformation },
    } = props;

    if (formData.appointmentInformation.notarization !== appointmentInformation.notarization) {
      const newFormData = props.values;
      if (appointmentInformation.notarization === 'IN_OFFICE') {
        // change from mobile to in office - remove address information
        newFormData.appointmentInformation.address = emptyAddress;
      } else {
        // change from in office to mobile - populate address with borrower preferred
        newFormData.appointmentInformation.address = borrowerPreferredAddress ?? emptyAddress;
      }
      // update form state
      setFormData(newFormData);
    }
  };

  const renderForm = (formProps: FormRenderProps<BlendBridge.API.ScheduleClosingTaskCompleteMeta>): ReactNode => (
    <>
      <form onSubmit={formProps.handleSubmit}>
        {renderAppointmentInformation(props, setError)}
        {renderNotaryInformation(setError)}
      </form>
    </>
  );

  const renderTaskModal = (
    formRenderProps: FormRenderProps<BlendBridge.API.ScheduleClosingTaskCompleteMeta>,
  ): ReactNode => {
    const taskModalProps: TaskModalProps = {
      divideFooter: true,
      error,
      handleClose,
      handleSubmit: formRenderProps.handleSubmit,
      isLoading,
      isOpen: isModalOpen,
      submitButtonLabel: 'Continue',
      taskId,
      title: taskHeader,
    };

    if (displayState === TaskDisplayStates.CONFIRM) {
      taskModalProps.alternateButtonLabel = 'Back';
      taskModalProps.submitButtonLabel = 'Schedule';
      taskModalProps.handleAlternateClick = onBack;
    }

    return (
      <TaskModal {...taskModalProps}>
        <FormSpy onChange={onFormChange} subscription={{ values: true }} />
        {displayState === TaskDisplayStates.CONFIRM && renderConfirmationScreen(props, formData)}
        {displayState === TaskDisplayStates.EDIT && renderForm(formRenderProps)}
      </TaskModal>
    );
  };

  return (
    <Task
      buttonLabel={buttonLabel}
      isDisabled={isDisabled}
      onClick={openModal}
      taskHeader={taskHeader}
      taskId={taskId}
      type={BlendBridge.TaskTypes.SCHEDULE_CLOSING_APPOINTMENT}
    >
      <Form initialValues={formData} onSubmit={onSubmit} render={renderTaskModal} />
    </Task>
  );
};
